// Copyright (c) Magnopus LLC. All Rights Reserved.

import type { Breadcrumb, BreadcrumbHint, EventHint, ErrorEvent } from '@sentry/react';
import type { RootOptions } from 'react-dom/client';
import { setTag } from '@sentry/react';
import * as Sentry from '@sentry/react';

import { cspVersion } from '@magnopus-opensource/connected-spaces-platform-web-api';

import { environment, ready, utils } from '@magnopus/env';
import { clientVersionString } from '@magnopus/utils';
import { sentryReplayCanvasConfig, requestSentryReplayCanvasSnapshot } from '@magnopus/shared-utils';

import packageJson from '../../package.json';

export const initSentry = (): Partial<RootOptions> => {
  const metadata = {
    buildMajor: parseInt(packageJson.version.split('.')[0]),
    buildMinor: parseInt(packageJson.version.split('.')[1]),
    buildRevision: parseInt(packageJson.version.split('.')[2])
  };

  const getClassOf = Function.prototype.call.bind(Object.prototype.toString);
  const utf8decoder = 'TextDecoder' in window ? new TextDecoder('utf-8') : undefined;
  const HEADER_SIGNATURES_TO_IGNORE = [
    0x676c5446, // GLB
    0x233f5241 // HDR / Cube maps
  ];
  const RESPONSE_LENGTH_LIMIT = 64000; // Somewhat of a magic number. Not tested higher than this but the fail threshold was over 80000

  if (utils.sentry.enabled) {
    ready().then(() => {
      Sentry.getCurrentScope().setTag('environment', environment.deploymentId);
    });

    setTag('CSP', cspVersion);
    let errorsReportedThisSession = 0;

    const decode = (value: any) => {
      let ret: string | undefined = undefined;

      // xhr request / response bodies from CSP are array buffers that need to be decoded
      if (value && utf8decoder) {
        const dataClass = getClassOf(value);

        if (dataClass === '[object ArrayBuffer]' || dataClass === '[object Uint8Array]') {
          try {
            const array = dataClass === '[object ArrayBuffer]' ? value : value.buffer;
            const headerValue = new DataView(array).getUint32(0);
            // Attempt ignore some binary content based on the first four bytes matching some known values
            // for GLB/GLTF, image files etc.. Add more magic numbers as needed
            if (!HEADER_SIGNATURES_TO_IGNORE.includes(headerValue)) {
              const decoded = utf8decoder.decode(value);
              ret = decoded;
            }
          } catch (e) {
            // console.log('Error decoding array value');
          }
        } else if (dataClass === '[object String]') {
          ret = value;
        }
        // otherwise ignore
      }
      return ret;
    };

    console.log(`%c Sentry %c ${Sentry.SDK_VERSION}`, 'color: #362d59; background: #eedbff;', '');

    Sentry.init({
      debug: false,
      dsn: utils.sentry.dsn,
      integrations: [
        // TODO: check this works as expected.
        // The tracingOrigins option was renamed to tracePropagationTargets (see below integrations option) and removed
        // in Sentry v8.
        // See https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#tracePropagationTargets
        Sentry.browserTracingIntegration(/*{ tracingOrigins: ['*'] }*/),
        // TODO: check this works as expected.
        // browserSessionIntegration was added to replace the autoSessionTracking option removed in Sentry v9.
        // See https://docs.sentry.io/platforms/javascript/migration/v8-to-v9/#removed-apis
        Sentry.browserSessionIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: true,
          maskAttributes: ['title', 'placeholder', 'aria-label', 'alt']
        }),
        ...(sentryReplayCanvasConfig.mode === 'off'
          ? []
          : [
              Sentry.replayCanvasIntegration({
                enableManualSnapshot: sentryReplayCanvasConfig.mode === 'manual',
                quality: sentryReplayCanvasConfig.quality
              })
            ])
      ],
      tracePropagationTargets: ['*'],
      environment: environment.deploymentId,
      release: clientVersionString(metadata),
      tracesSampleRate: utils.sentry.tracesSampleRate,
      dataCollection: { userInfo: environment.type !== 'prod' },
      attachStacktrace: true,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeBreadcrumb(breadcrumb: Breadcrumb, hint: BreadcrumbHint | undefined) {
        //if more that 20 errors have been reported this session, return the standard breadcrumb
        if (errorsReportedThisSession > 20) {
          return breadcrumb;
        }

        if (breadcrumb.category === 'xhr' && hint) {
          const decodedBody = decode(hint.xhr.__sentry_xhr_v3__.body);
          const decodedResponse = decode(hint.xhr.response);

          // If the decodedResponse length is greater than the limit then don't send it
          // This is the prevent the 400 errors from Sentry around envelope size
          if (decodedBody && decodedResponse) {
            const data = {
              requestBody: decodedBody,
              response: decodedResponse.length < RESPONSE_LENGTH_LIMIT ? decodedResponse : undefined,
              responseUrl: hint.xhr.responseURL
            };

            return { ...breadcrumb, data };
          }
        }

        return breadcrumb;
      },
      beforeSend(event: ErrorEvent, host: EventHint) {
        errorsReportedThisSession++;

        if (errorsReportedThisSession > 20) {
          //if more that 20 errors have been reported this session, stop reporting issues
          return null;
        }

        // if the event request includes form data then clear the data
        if (event.request?.headers && event.request.headers['content-type']) {
          if (event.request.headers['content-type'].includes('multipart/form-data')) {
            event.request.data = null;
          }
        }

        // // Don't send username / email address on live environments.
        // if (environment.type === 'prod' && event.user) {
        //   delete event.user.email;
        //   delete event.user.username;
        // }

        // if (event.exception) {
        //   //cors errors (would be nice)
        //   showReportDialog();
        // }

        requestSentryReplayCanvasSnapshot();

        return event;
      }
    });

    // Deliberately no onCaughtError: errors caught by a boundary are already reported by the boundary itself (the
    // shared-ui ErrorBoundary, and Sentry.ErrorBoundary at the app root), so adding it here would report every one
    // of them twice. This only covers errors that no boundary catches.
    return {
      onUncaughtError: Sentry.reactErrorHandler()
    };
  }

  return {};
};
